<template>
    <n-config-provider
        :date-locale="localeObj[localeStr].date"
        :locale="localeObj[localeStr].lang"
        :theme-overrides="theme"
    >
        <n-dialog-provider>
            <n-message-provider>
                <main font-sans text="gray-700 dark:gray-200">
                    <component
                        :is="
                            showThemeEditor && localFlagShowThemeEditor
                                ? NThemeEditor
                                : 'div-container'
                        "
                    >
                        <n-loading-bar-provider>
                            <n-message-provider>
                                <n-dialog-provider>
                                    <router-view />
                                </n-dialog-provider>
                            </n-message-provider>
                        </n-loading-bar-provider>
                    </component>
                </main>
            </n-message-provider>
        </n-dialog-provider>
    </n-config-provider>
</template>

<script lang="ts" setup>
import type { Ref } from 'vue';
import {
    NThemeEditor,
    csCZ,
    dateCsCZ,
    dateDeDE,
    dateEnUS,
    dateEsAR,
    dateEtEE,
    dateFrFR,
    dateItIT,
    dateNlNL,
    datePlPL,
    datePtBR,
    dateRuRU,
    dateSkSK,
    dateSvSE,
    dateThTH,
    dateTrTR,
    dateUkUA,
    dateViVN,
    dateZhCN,
    dateZhTW,
    deDE,
    enUS,
    esAR,
    etEE,
    frFR,
    itIT,
    nlNL,
    plPL,
    ptBR,
    ruRU,
    skSK,
    svSE,
    thTH,
    trTR,
    ukUA,
    viVN,
    zhCN,
    zhTW,
} from 'naive-ui';
import huHU from '../locales/hu-HU';
import arSA from '../locales/ar-SA';
import bgBG from '../locales/bg-BG';
import daDK from '../locales/da-DK';
import fiFI from '../locales/fi-FI';
import hrHR from '../locales/hr-HR';
import lvLV from '../locales/lv-LV';
import ltLT from '../locales/lt-LT';
import roRO from '../locales/ro-RO';
import nbNO from '../locales/nb-NO';
import ptBX from '../locales/pt-BX';
import slSI from '../locales/sl-SI';
import elGR from '../locales/el-GR';
import kkKZ from '../locales/kk-KZ';

import theme from './styles/naive-ui-theme-overrides.json';
import { useThemeditorVisible } from './composables/useThemeEditor';
import { Locales } from './composables/i18n';

const { locale } = useTypedI18n();
const localeStr = locale as Ref<Locales>;
const localeObj = shallowRef({
    [Locales.zhCN]: {
        lang: zhCN,
        date: dateZhCN,
    },
    [Locales.enUS]: {
        lang: enUS,
        date: dateEnUS,
    },
    [Locales.deDE]: {
        lang: deDE,
        date: dateDeDE,
    },
    [Locales.frFR]: {
        lang: frFR,
        date: dateFrFR,
    },
    [Locales.nlNL]: {
        lang: nlNL,
        date: dateNlNL,
    },
    [Locales.itIT]: {
        lang: itIT,
        date: dateItIT,
    },
    [Locales.viVN]: {
        lang: viVN,
        date: dateViVN,
    },
    [Locales.esES]: {
        lang: esAR,
        date: dateEsAR,
    },
    [Locales.plPL]: {
        lang: plPL,
        date: datePlPL,
    },
    [Locales.ptPT]: {
        lang: ptBR,
        date: datePtBR,
    },
    [Locales.trTR]: {
        lang: trTR,
        date: dateTrTR,
    },
    [Locales.csCZ]: {
        lang: csCZ,
        date: dateCsCZ,
    },
    [Locales.huHU]: {
        lang: huHU,
        date: dateEnUS,
    },
    [Locales.ruRU]: {
        lang: ruRU,
        date: dateRuRU,
    },
    [Locales.zhTW]: {
        lang: zhTW,
        date: dateZhTW,
    },
    [Locales.svSE]: {
        lang: svSE,
        date: dateSvSE,
    },
    [Locales.skSK]: {
        lang: skSK,
        date: dateSkSK,
    },
    [Locales.thTH]: {
        lang: thTH,
        date: dateThTH,
    },
    [Locales.ukUA]: {
        lang: ukUA,
        date: dateUkUA,
    },
    [Locales.etEE]: {
        lang: etEE,
        date: dateEtEE,
    },
    [Locales.arSA]: {
        lang: arSA,
        date: dateEnUS,
    },
    [Locales.bgBG]: {
        lang: bgBG,
        date: dateEnUS,
    },
    [Locales.daDK]: {
        lang: daDK,
        date: dateEnUS,
    },
    [Locales.fiFI]: {
        lang: fiFI,
        date: dateEnUS,
    },
    [Locales.hrHR]: {
        lang: hrHR,
        date: dateEnUS,
    },
    [Locales.lvLV]: {
        lang: lvLV,
        date: dateEnUS,
    },
    [Locales.ltLT]: {
        lang: ltLT,
        date: dateEnUS,
    },
    [Locales.roRO]: {
        lang: roRO,
        date: dateEnUS,
    },
    [Locales.nbNO]: {
        lang: nbNO,
        date: dateEnUS,
    },
    [Locales.ptBR]: {
        lang: ptBX,
        date: dateEnUS,
    },
    [Locales.slSI]: {
        lang: slSI,
        date: dateEnUS,
    },
    [Locales.elGR]: {
        lang: elGR,
        date: dateEnUS,
    },
    [Locales.kkKZ]: {
        lang: kkKZ,
        date: dateEnUS,
    },
});

const { localFlagShowThemeEditor, showThemeEditor } = useThemeditorVisible();
</script>

<style scoped></style>
